import { getCards, isNaturalRank, isWildcard, naturalRankValue, rankStrength, resolveRuleProfile, } from "./cards.js";
import { NATURAL_RANKS, } from "./types.js";
const sequenceRanks = Object.freeze([
    ["A", "2", "3", "4", "5"],
    ...Array.from({ length: 9 }, (_, index) => NATURAL_RANKS.slice(index, index + 5)),
]);
function assignmentKey(assignments) {
    return assignments
        .map((assignment) => `${assignment.cardId}:${assignment.asRank}`)
        .sort()
        .join(",");
}
function makeCombination(cards, candidate) {
    const cardIds = Object.freeze(cards
        .map((card) => card.id)
        .sort((left, right) => Number(left.slice(1)) - Number(right.slice(1))));
    const wildcardAssignments = Object.freeze([...candidate.assignments].sort((left, right) => Number(left.cardId.slice(1)) - Number(right.cardId.slice(1))));
    const normalizationKey = [
        candidate.kind,
        candidate.bombSize,
        candidate.mainValue,
        candidate.mainRank ?? "-",
        assignmentKey(wildcardAssignments),
    ].join("|");
    return Object.freeze({
        kind: candidate.kind,
        cardIds,
        mainRank: candidate.mainRank,
        mainValue: candidate.mainValue,
        cardCount: cards.length,
        bombSize: candidate.bombSize,
        isBomb: candidate.isBomb,
        wildcardAssignments,
        normalizationKey,
    });
}
function groupCandidate(cards, count, context) {
    if (cards.length !== count)
        return null;
    if (count === 2 &&
        cards.every((card) => card.rank === "SJ" || card.rank === "BJ") &&
        cards[0]?.rank === cards[1]?.rank) {
        const jokerRank = cards[0]?.rank;
        if (jokerRank !== "SJ" && jokerRank !== "BJ")
            return null;
        return {
            kind: "pair",
            mainRank: null,
            mainValue: rankStrength(jokerRank, context.levelRank),
            bombSize: 0,
            isBomb: false,
            assignments: [],
        };
    }
    if (cards.some((card) => !isNaturalRank(card.rank)))
        return null;
    const wildcards = cards.filter((card) => isWildcard(card, context.levelRank));
    const naturalCards = cards.filter((card) => !isWildcard(card, context.levelRank));
    const naturalRanks = new Set(naturalCards.map((card) => card.rank));
    if (naturalRanks.size > 1)
        return null;
    const mainRank = naturalCards[0]?.rank ?? context.levelRank;
    if (!isNaturalRank(mainRank))
        return null;
    return {
        kind: count === 2 ? "pair" : "triple",
        mainRank,
        mainValue: rankStrength(mainRank, context.levelRank),
        bombSize: 0,
        isBomb: false,
        assignments: wildcards.map((card) => ({
            cardId: card.id,
            asRank: mainRank,
        })),
    };
}
function bombCandidate(cards, context) {
    if (cards.length < 4 ||
        cards.length > 10 ||
        cards.some((card) => !isNaturalRank(card.rank)))
        return null;
    const wildcards = cards.filter((card) => isWildcard(card, context.levelRank));
    const naturalCards = cards.filter((card) => !isWildcard(card, context.levelRank));
    const naturalRanks = new Set(naturalCards.map((card) => card.rank));
    if (naturalRanks.size !== 1)
        return null;
    const mainRank = naturalCards[0]?.rank;
    if (!mainRank || !isNaturalRank(mainRank))
        return null;
    return {
        kind: "bomb",
        mainRank,
        mainValue: rankStrength(mainRank, context.levelRank),
        bombSize: cards.length,
        isBomb: true,
        assignments: wildcards.map((card) => ({
            cardId: card.id,
            asRank: mainRank,
        })),
    };
}
function jokerBombCandidate(cards) {
    if (cards.length !== 4)
        return null;
    const ranks = cards.map((card) => card.rank);
    if (ranks.filter((rank) => rank === "SJ").length !== 2 ||
        ranks.filter((rank) => rank === "BJ").length !== 2)
        return null;
    return {
        kind: "joker-bomb",
        mainRank: null,
        mainValue: 100,
        bombSize: 4,
        isBomb: true,
        assignments: [],
    };
}
function sequenceCandidate(cards, context, kind, multiplicity, sequenceLength) {
    if (cards.length !== multiplicity * sequenceLength ||
        cards.some((card) => !isNaturalRank(card.rank)))
        return null;
    const wildcards = cards.filter((card) => isWildcard(card, context.levelRank));
    const orderedWildcards = [...wildcards].sort((left, right) => Number(left.id.slice(1)) - Number(right.id.slice(1)));
    const naturalCards = cards.filter((card) => !isWildcard(card, context.levelRank));
    const candidates = [];
    const sequences = sequenceLength === 5
        ? sequenceRanks
        : Array.from({ length: NATURAL_RANKS.length - sequenceLength + 1 }, (_, index) => NATURAL_RANKS.slice(index, index + sequenceLength));
    for (const ranks of sequences) {
        if (ranks[0] === "A" &&
            !resolveRuleProfile(context.ruleProfile).allowA2345Straight)
            continue;
        const counts = new Map();
        let invalid = false;
        for (const card of naturalCards) {
            // Only the heart level card is a wildcard. Level cards in the other
            // suits keep their printed rank and may participate naturally in a
            // sequence (for example A-2-3-4-5 while playing level 2).
            if (!isNaturalRank(card.rank) || !ranks.includes(card.rank)) {
                invalid = true;
                break;
            }
            counts.set(card.rank, (counts.get(card.rank) ?? 0) + 1);
        }
        if (invalid || [...counts.values()].some((count) => count > multiplicity))
            continue;
        const missingRanks = [];
        for (const rank of ranks) {
            const missing = multiplicity - (counts.get(rank) ?? 0);
            for (let index = 0; index < missing; index += 1)
                missingRanks.push(rank);
        }
        if (missingRanks.length !== wildcards.length)
            continue;
        const assignments = orderedWildcards
            .map((card, index) => ({
            cardId: card.id,
            asRank: missingRanks[index],
        }))
            .sort((left, right) => naturalRankValue(left.asRank) - naturalRankValue(right.asRank));
        const wheel = ranks[0] === "A";
        const highRank = wheel ? "5" : ranks[ranks.length - 1];
        candidates.push({
            kind,
            mainRank: highRank,
            mainValue: naturalRankValue(highRank),
            bombSize: 0,
            isBomb: false,
            assignments,
        });
    }
    return candidates.sort(compareCandidateNormalization).at(-1) ?? null;
}
function straightFlushCandidate(cards, context) {
    if (cards.length !== 5)
        return null;
    const profile = resolveRuleProfile(context.ruleProfile);
    const wildcards = cards.filter((card) => isWildcard(card, context.levelRank));
    if (wildcards.length > 0 && !profile.wildcardInStraightFlush)
        return null;
    const naturalCards = cards.filter((card) => !isWildcard(card, context.levelRank));
    if (naturalCards.some((card) => !isNaturalRank(card.rank)))
        return null;
    const suit = naturalCards[0]?.suit ?? wildcards[0]?.suit;
    // A heart level card adopts both the missing rank and the natural cards'
    // suit. Only the non-wild cards must therefore share a suit.
    if (!suit ||
        suit === "joker" ||
        naturalCards.some((card) => card.suit !== suit))
        return null;
    const base = sequenceCandidate(cards, context, "straight", 1, 5);
    if (!base)
        return null;
    return { ...base, kind: "straight-flush", isBomb: true, bombSize: 5 };
}
function fullHouseCandidates(cards, context) {
    if (cards.length !== 5)
        return [];
    const jokerCards = cards.filter((card) => card.rank === "SJ" || card.rank === "BJ");
    if (jokerCards.length > 0) {
        if (jokerCards.length !== 2 || jokerCards[0]?.rank !== jokerCards[1]?.rank)
            return [];
        const triple = groupCandidate(cards.filter((card) => card.rank !== "SJ" && card.rank !== "BJ"), 3, context);
        if (!triple || triple.kind !== "triple")
            return [];
        return [
            {
                ...triple,
                kind: "full-house",
            },
        ];
    }
    if (cards.some((card) => !isNaturalRank(card.rank)))
        return [];
    const wildcards = cards.filter((card) => isWildcard(card, context.levelRank));
    const orderedWildcards = [...wildcards].sort((left, right) => Number(left.id.slice(1)) - Number(right.id.slice(1)));
    const naturalCards = cards.filter((card) => !isWildcard(card, context.levelRank));
    const candidates = [];
    for (const tripleRank of NATURAL_RANKS) {
        for (const pairRank of NATURAL_RANKS) {
            if (tripleRank === pairRank)
                continue;
            const naturalTriple = naturalCards.filter((card) => card.rank === tripleRank);
            const naturalPair = naturalCards.filter((card) => card.rank === pairRank);
            if (naturalTriple.length + naturalPair.length !== naturalCards.length)
                continue;
            if (naturalTriple.length > 3 || naturalPair.length > 2)
                continue;
            const tripleMissing = 3 - naturalTriple.length;
            const pairMissing = 2 - naturalPair.length;
            if (tripleMissing + pairMissing !== wildcards.length)
                continue;
            const assignments = orderedWildcards.map((card, index) => ({
                cardId: card.id,
                asRank: index < tripleMissing ? tripleRank : pairRank,
            }));
            candidates.push({
                kind: "full-house",
                mainRank: tripleRank,
                mainValue: rankStrength(tripleRank, context.levelRank),
                bombSize: 0,
                isBomb: false,
                assignments,
            });
        }
    }
    return candidates;
}
function compareCandidateNormalization(left, right) {
    if (left.mainValue !== right.mainValue)
        return left.mainValue - right.mainValue;
    if (left.bombSize !== right.bombSize)
        return left.bombSize - right.bombSize;
    return assignmentKey(left.assignments).localeCompare(assignmentKey(right.assignments));
}
export function classifyCombination(cardLikes, context) {
    const cards = getCards(cardLikes);
    if (cards.length === 0)
        return null;
    const candidates = [];
    const jokerBomb = jokerBombCandidate(cards);
    if (jokerBomb)
        candidates.push(jokerBomb);
    const straightFlush = straightFlushCandidate(cards, context);
    if (straightFlush)
        candidates.push(straightFlush);
    const bomb = bombCandidate(cards, context);
    if (bomb)
        candidates.push(bomb);
    if (cards.length === 1) {
        const card = cards[0];
        candidates.push({
            kind: "single",
            mainRank: isNaturalRank(card.rank) ? card.rank : null,
            mainValue: rankStrength(card.rank, context.levelRank),
            bombSize: 0,
            isBomb: false,
            assignments: [],
        });
    }
    const pair = groupCandidate(cards, 2, context);
    if (pair)
        candidates.push(pair);
    const triple = groupCandidate(cards, 3, context);
    if (triple)
        candidates.push(triple);
    candidates.push(...fullHouseCandidates(cards, context));
    const straight = sequenceCandidate(cards, context, "straight", 1, 5);
    if (straight)
        candidates.push(straight);
    const consecutivePairs = sequenceCandidate(cards, context, "consecutive-pairs", 2, 3);
    if (consecutivePairs)
        candidates.push(consecutivePairs);
    const steelPlate = sequenceCandidate(cards, context, "steel-plate", 3, 2);
    if (steelPlate)
        candidates.push(steelPlate);
    const priority = {
        single: 0,
        pair: 1,
        triple: 2,
        "full-house": 3,
        straight: 4,
        "consecutive-pairs": 5,
        "steel-plate": 6,
        bomb: 7,
        "straight-flush": 8,
        "joker-bomb": 9,
    };
    const best = candidates
        .sort((left, right) => {
        const kind = priority[left.kind] - priority[right.kind];
        return kind === 0 ? compareCandidateNormalization(left, right) : kind;
    })
        .at(-1);
    return best ? makeCombination(cards, best) : null;
}
function bombTier(combination, context) {
    if (combination.kind === "joker-bomb")
        return 100;
    const threshold = resolveRuleProfile(context?.ruleProfile).straightFlushBeatsBombSize;
    if (combination.kind === "straight-flush")
        return threshold * 2 + 1;
    return combination.bombSize * 2;
}
export function compareCombinations(challenger, incumbent, context) {
    if (challenger.isBomb || incumbent.isBomb) {
        if (!challenger.isBomb)
            return -1;
        if (!incumbent.isBomb)
            return 1;
        const tier = bombTier(challenger, context) - bombTier(incumbent, context);
        if (tier !== 0)
            return tier;
        return challenger.mainValue - incumbent.mainValue;
    }
    if (challenger.kind !== incumbent.kind ||
        challenger.cardCount !== incumbent.cardCount)
        return -1;
    return challenger.mainValue - incumbent.mainValue;
}
export function combinationBeats(challenger, incumbent, context) {
    return (incumbent === null ||
        compareCombinations(challenger, incumbent, context) > 0);
}
//# sourceMappingURL=combinations.js.map